import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  Injector,
  signal,
  viewChild,
  WritableSignal
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { I18NextPipe, I18NextService } from 'angular-i18next';
import { isNil } from 'lodash';
import { ButtonComponent, ModalComponent, ModalFooterDirective } from 'mbs-ui-kit';
import { ScheduleStepValue } from 'mbs-ui-shared@models/backup/schedule-models';
import { CompanyDestination } from 'mbs-ui-shared@models/Company';
import { SAWithDestinations } from 'mbs-ui-shared@models/StorageAccountsWithDestinations';
import { StorageConnection } from 'mbs-ui-shared@models/storge-connections';
import { AdvancedStorageSettings } from 'wizards/helpers/support-methods-for-steps-from-plan';
import { AdvancedOptionsFlags, AdvancedOptionsStepValue } from 'wizards/models/advanced-options-models';
import { Storages } from 'wizards/models/base/base-models/plan-storages-model';
import { DestinationPlusRetentionStepValue } from 'wizards/models/combined/destination-plus-retention-model';
import { RemoteManagementWizardsService } from 'wizards/services/remote-management-wizards.service';
import { WizardStepsService } from 'wizards/services/wizard-steps.service';
import { AdvancedOptionsStepComponent } from 'wizards/steps/advanced-options-step/advanced-options-step.component';
import { DestinationPlusRetentionStepComponent } from 'wizards/steps/combined/destination-plus-retention-step/destination-plus-retention-step.component';
import { CompanyBackupTemplate } from '../../models/company-wizard-models';
import {
  applyTemplateScheduleContext,
  applyTemplateStorageContext,
  borrowPlanContext,
  companyDestinationsToStorages,
  companyStorageConnections,
  planContextFromTemplate,
  templateAdvancedOptionsFlags,
  templateAdvancedOptionsNeedShow,
  templateAdvancedSchedule
} from '../../utils/company-wizard-plan-context.util';
import { templateRetentionProblems } from '../../utils/company-wizard-template-validation.util';

export type BackupDestinationSettingsModalData = {
  companyDestinations: CompanyDestination[];
  availableStorages: SAWithDestinations[];
  template: CompanyBackupTemplate;
};

export type BackupDestinationSettingsModalResult = {
  advancedOptions: AdvancedOptionsStepValue;
  destinationPlusRetention: DestinationPlusRetentionStepValue;
  schedule?: string;
  scheduleStep?: ScheduleStepValue;
};

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AdvancedOptionsStepComponent,
    ButtonComponent,
    DestinationPlusRetentionStepComponent,
    FormsModule,
    I18NextPipe,
    ModalComponent,
    ModalFooterDirective
  ],
  selector: 'mbs-backup-destination-settings-modal',
  standalone: true,
  templateUrl: './backup-destination-settings-modal.component.html'
})
export class BackupDestinationSettingsModalComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly i18nPipe = inject(I18NextPipe);
  private readonly i18nextService = inject(I18NextService);
  private readonly injector = inject(Injector);
  private readonly mainService = inject(RemoteManagementWizardsService);
  private readonly stepService = inject(WizardStepsService);

  protected readonly advancedOptionsStep = viewChild(AdvancedOptionsStepComponent);
  protected readonly baseModal = viewChild(ModalComponent);
  protected readonly destinationPlusRetentionStep = viewChild(DestinationPlusRetentionStepComponent);

  public readonly advancedOptions: WritableSignal<AdvancedOptionsStepValue> = signal(null);
  public readonly advancedOptionsAvailable: WritableSignal<boolean> = signal(false);
  public readonly advancedOptionsFlags: WritableSignal<AdvancedOptionsFlags> = signal(null);
  public readonly destinationPlusRetention: WritableSignal<DestinationPlusRetentionStepValue> = signal(null);
  public readonly ready: WritableSignal<boolean> = signal(false);
  public readonly storages: WritableSignal<Storages> = signal(null);

  private readonly advancedStorage: WritableSignal<AdvancedStorageSettings> = signal(null);
  private readonly connection: WritableSignal<StorageConnection> = signal(null);
  private readonly template: WritableSignal<CompanyBackupTemplate> = signal(null);

  public readonly advancedOptionsTitle = this.i18nPipe.transform('wizards:advanced_options_title');

  constructor() {
    // ModalService assigns data only after open() returns, and a signal viewChild is unresolved until the view is checked
    afterNextRender(() => this.applyModalData());
  }

  public advancedOptionsOpenedHandler(opened: boolean): void {
    const step = this.advancedOptionsStep();

    if (step) step.stepFocused = opened;
  }

  public changeStorageHandler(connection: StorageConnection): void {
    if (connection?.ID !== this.connection()?.ID) this.stepService.updateProperty('wizard', 'userInteractWithSteps', true);

    this.connection.set(connection);
    this.applyAdvancedOptionsFlags();
    this.applyStorageContext();
  }

  public changeStorageTypeHandler(event: AdvancedStorageSettings): void {
    this.advancedStorage.set(event);
    this.applyStorageContext();
  }

  public saveClickHandler(): void {
    const destinationPlusRetention = this.withIntelligentRetention(this.destinationPlusRetention());
    const result = {
      advancedOptions: this.withVssForFastNtfsScan(this.advancedOptions()),
      destinationPlusRetention,
      ...this.scheduleRequiredByGFS(destinationPlusRetention)
    } as BackupDestinationSettingsModalResult;

    if (!this.canSave(result)) return void this.destinationPlusRetentionStep()?.forceValid();

    this.baseModal().save(result);
  }

  public cancelClickHandler(): void {
    this.baseModal().close();
  }

  // Private section
  // The step disables Force VSS while fast NTFS scan is on, so its value drops the field; plan wizards get it back from the plan
  // the same way (StepsDataFromPlanHelper) whenever the step is shown again
  // The retention form has no intelligentRetention control, so the step's value drops the flag the plan turns into
  // AutomaticallyReduceEarlyDeletionFee
  private withIntelligentRetention(value: DestinationPlusRetentionStepValue): DestinationPlusRetentionStepValue {
    const intelligentRetention = this.template()?.destinationPlusRetention?.retentionPolicyStep?.intelligentRetention;

    if (!value?.retentionPolicyStep || isNil(intelligentRetention)) return value;

    return { ...value, retentionPolicyStep: { ...value.retentionPolicyStep, intelligentRetention } };
  }

  private withVssForFastNtfsScan(options: AdvancedOptionsStepValue): AdvancedOptionsStepValue {
    return options?.UseFastNtfsScan ? { ...options, AlwaysUseVSS: true } : options;
  }

  private applyModalData(): void {
    const data = this.baseModal()?.data as BackupDestinationSettingsModalData;

    if (!data) return;

    const restore = borrowPlanContext(this.mainService, planContextFromTemplate(data.template));

    this.destroyRef.onDestroy(restore);

    const storages = companyDestinationsToStorages(data.companyDestinations, data.availableStorages);
    const selected = companyStorageConnections(data.companyDestinations, data.availableStorages).find(
      ({ ID }) => ID === data.template.destinationPlusRetention?.whereBackupStep?.ConnectionID
    );

    this.storages.set(storages);
    this.template.set(data.template);
    this.advancedOptions.set(data.template.advancedOptions);
    this.advancedStorage.set({
      azureType: data.template.advancedOptions?.azureAccessTier ?? null,
      storageClass: data.template.advancedOptions?.StorageClass ?? null
    });
    this.connection.set(selected ?? null);
    this.applyAdvancedOptionsFlags();
    this.destinationPlusRetention.set(data.template.destinationPlusRetention);
    this.ready.set(true);

    // pipeProperty does not replay, and the hosted steps subscribe as they are created, so the calculated properties
    // have to be published once the step exists
    afterNextRender(
      () => {
        this.applyStorageContext();
        applyTemplateScheduleContext(this.stepService, data.template.scheduleStep);
        setTimeout(() => {
          this.restoreSyntheticFull(data.template.advancedOptions?.SyntheticFull);
          // The step's retention logic starts listening once the step is handed its value, after the publish above
          applyTemplateScheduleContext(this.stepService, data.template.scheduleStep);
        });
      },
      { injector: this.injector }
    );
  }

  // In create mode the step turns Synthetic Full on whenever it is handed a value, which a plan wizard does once and this modal
  // does on every open; the timer lands after the step's own create-mode setTimeout (updateFormState)
  private restoreSyntheticFull(syntheticFull: boolean): void {
    const control = this.advancedOptionsStep()?.stepForm?.get('SyntheticFull');

    if (isNil(syntheticFull) || !control || control.value === syntheticFull) return;

    control.setValue(syntheticFull);
  }

  private canSave(result: BackupDestinationSettingsModalResult): boolean {
    const retentionForm = this.destinationPlusRetentionStep()?.stepForm?.get('retentionPolicyStep');

    return retentionForm?.valid !== false && !templateRetentionProblems({ ...this.template(), ...result }, this.connection()).length;
  }

  private scheduleRequiredByGFS(value: DestinationPlusRetentionStepValue): Partial<BackupDestinationSettingsModalResult> {
    const template = this.template();

    if (!value?.retentionPolicyStep?.GFSPolicySettings?.enabled || template.scheduleStep?.ScheduleType === 'predefined') return {};

    return templateAdvancedSchedule(template, this.i18nextService);
  }

  private applyAdvancedOptionsFlags(): void {
    const flags = templateAdvancedOptionsFlags(this.template(), this.connection(), this.mainService.backupVersionUpdated);

    this.advancedOptionsAvailable.set(templateAdvancedOptionsNeedShow(flags, this.template().planType));
    this.advancedOptionsFlags.set(flags);
  }

  private applyStorageContext(): void {
    applyTemplateStorageContext(this.stepService, {
      advancedStorage: this.advancedStorage(),
      agentVersion: this.mainService.backupVersionUpdated,
      connection: this.connection(),
      storages: this.storages()
    });
  }
}
